import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightning, Broadcast, ShieldCheck, ArrowsClockwise,
  ChartLineUp, Heartbeat, Funnel, MagnifyingGlass,
  PaperPlaneRight, Archive, CheckCircle, Warning,
  CaretDown, Eye
} from '@phosphor-icons/react';

const SIGNAL_TYPE_META = {
  anomaly_pattern: { label: 'Anomaly Pattern', icon: Warning, color: 'kova-red' },
  performance_baseline: { label: 'Performance Baseline', icon: ChartLineUp, color: 'kova-cyan' },
  schema_drift: { label: 'Schema Drift', icon: ArrowsClockwise, color: 'kova-amber' },
  error_correlation: { label: 'Error Correlation', icon: Lightning, color: 'kova-purple' },
  traffic_pattern: { label: 'Traffic Pattern', icon: Broadcast, color: 'kova-green' },
  integration_health: { label: 'Integration Health', icon: Heartbeat, color: 'kova-gold' },
};

const SEVERITY_STYLES = {
  low: 'bg-kova-cyan/10 text-kova-cyan border-kova-cyan/20',
  medium: 'bg-kova-amber/10 text-kova-amber border-kova-amber/20',
  high: 'bg-kova-red/10 text-kova-red border-kova-red/20',
  critical: 'bg-kova-red/20 text-kova-red border-kova-red/40',
};

export default function SignalExchangePage() {
  const [tab, setTab] = useState('feed');
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [ownSignals, setOwnSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [showCapture, setShowCapture] = useState(false);
  const [accessError, setAccessError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setAccessError(null);
    try {
      const [statsRes, feedRes, ownRes] = await Promise.all([
        fetch(`${API}/signals/stats`, { credentials: 'include' }),
        fetch(`${API}/signals/feed?${new URLSearchParams({ ...(filterType && { signal_type: filterType }), ...(filterSeverity && { severity: filterSeverity }) })}`, { credentials: 'include' }),
        fetch(`${API}/signals`, { credentials: 'include' }),
      ]);

      if (statsRes.status === 403) {
        const err = await statsRes.json();
        setAccessError(err.detail);
        setLoading(false);
        return;
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (feedRes.ok) setFeed(await feedRes.json());
      if (ownRes.ok) setOwnSignals(await ownRes.json());
    } catch (e) {
      console.error('Signal fetch error:', e);
    }
    setLoading(false);
  }, [filterType, filterSeverity]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAcknowledge = async (signalId) => {
    const res = await fetch(`${API}/signals/${signalId}/acknowledge`, { method: 'PATCH', credentials: 'include' });
    if (res.ok) fetchData();
  };

  const handleArchive = async (signalId) => {
    const res = await fetch(`${API}/signals/${signalId}/archive`, { method: 'PATCH', credentials: 'include' });
    if (res.ok) fetchData();
  };

  if (accessError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="signal-access-error">
        <div className="text-center max-w-md">
          <ShieldCheck size={48} className="mx-auto mb-4 text-kova-slate" />
          <h2 className="font-heading text-xl font-bold text-kova-white mb-2">Signal Exchange Locked</h2>
          <p className="text-kova-silv text-sm mb-4">{accessError}</p>
          <a href="/subscription" className="inline-block font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors">
            View Plans
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signal-exchange-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-kova-white tracking-tight">Signal Exchange</h1>
          <p className="font-mono text-[10px] tracking-[2px] uppercase text-kova-slate mt-1">F-010 Distributed Neural Matching</p>
        </div>
        <button
          data-testid="capture-signal-btn"
          onClick={() => setShowCapture(true)}
          className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors cursor-crosshair"
        >
          <PaperPlaneRight size={14} weight="bold" />
          Capture Signal
        </button>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-kova-cyan/12">
          <StatTile label="Own Signals" value={stats.own_signals?.total ?? 0} />
          <StatTile label="Feed Available" value={stats.feed?.total ?? 0} />
          <StatTile label="Acks Given" value={stats.acknowledgments?.given ?? 0} />
          <StatTile label="Acks Received" value={stats.acknowledgments?.received ?? 0} />
        </div>
      )}

      {/* Signal Type Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-kova-cyan/12">
          {Object.entries(SIGNAL_TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const count = stats.own_signals?.by_type?.[key] ?? 0;
            return (
              <button
                key={key}
                data-testid={`signal-type-${key}`}
                onClick={() => setFilterType(filterType === key ? '' : key)}
                className={`bg-kova-card p-4 text-left transition-colors cursor-crosshair ${filterType === key ? 'bg-kova-card2' : 'hover:bg-kova-card2'}`}
              >
                <Icon size={20} className={`text-${meta.color} mb-2`} />
                <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate">{meta.label}</p>
                <p className="font-heading text-lg font-bold text-kova-white mt-1">{count}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-kova-cyan/12">
        {['feed', 'own'].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 font-mono text-[10px] tracking-[2px] uppercase transition-colors ${
              tab === t ? 'text-kova-cyan border-b-2 border-kova-cyan' : 'text-kova-slate hover:text-kova-silv'
            }`}
          >
            {t === 'feed' ? 'Exchange Feed' : 'My Signals'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Funnel size={14} className="text-kova-slate" />
        <select
          data-testid="filter-severity"
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="bg-kova-card border border-kova-cyan/12 text-kova-silv font-mono text-[10px] tracking-wide px-3 py-1.5 focus:outline-none focus:border-kova-cyan"
        >
          <option value="">All Severities</option>
          {['low', 'medium', 'high', 'critical'].map((s) => (
            <option key={s} value={s}>{s.toUpperCase()}</option>
          ))}
        </select>
        {(filterType || filterSeverity) && (
          <button
            onClick={() => { setFilterType(''); setFilterSeverity(''); }}
            className="text-kova-slate hover:text-kova-silv font-mono text-[9px] tracking-wide"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Signal List */}
      {loading ? (
        <div className="text-center py-12 text-kova-slate font-mono text-xs">Loading signals...</div>
      ) : (
        <div className="space-y-1">
          {(tab === 'feed' ? feed : ownSignals).length === 0 ? (
            <div className="text-center py-12 border border-kova-cyan/12 bg-kova-card">
              <Broadcast size={32} className="mx-auto mb-3 text-kova-slate" />
              <p className="text-kova-silv text-sm">{tab === 'feed' ? 'No signals in the exchange feed yet' : 'You haven\'t captured any signals yet'}</p>
            </div>
          ) : (
            (tab === 'feed' ? feed : ownSignals).map((signal) => (
              <SignalCard
                key={signal.signal_id}
                signal={signal}
                isFeed={tab === 'feed'}
                onAcknowledge={handleAcknowledge}
                onArchive={handleArchive}
              />
            ))
          )}
        </div>
      )}

      {/* Capture Modal */}
      <AnimatePresence>
        {showCapture && (
          <CaptureModal onClose={() => setShowCapture(false)} onSuccess={() => { setShowCapture(false); fetchData(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="bg-kova-card p-4">
      <p className="font-heading text-xl font-bold text-kova-white">{value}</p>
      <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-1">{label}</p>
    </div>
  );
}

function SignalCard({ signal, isFeed, onAcknowledge, onArchive }) {
  const meta = SIGNAL_TYPE_META[signal.signal_type] || SIGNAL_TYPE_META.anomaly_pattern;
  const Icon = meta.icon;
  const sevStyle = SEVERITY_STYLES[signal.severity] || SEVERITY_STYLES.medium;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-kova-card border border-kova-cyan/12 p-4 hover:border-kova-cyan/25 transition-colors group"
      data-testid={`signal-card-${signal.signal_id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Icon size={18} className={`text-${meta.color} mt-0.5 flex-shrink-0`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`inline-block border px-1.5 py-0.5 font-mono text-[9px] tracking-wide ${sevStyle}`}>
                {signal.severity?.toUpperCase()}
              </span>
              <span className="font-mono text-[9px] text-kova-slate tracking-wide">{meta.label}</span>
              {isFeed && (
                <span className="font-mono text-[8px] text-kova-slate tracking-wider">
                  src:{signal.source_hash}
                </span>
              )}
            </div>
            <h3 className="font-heading text-sm font-bold text-kova-white truncate">{signal.title}</h3>
            {signal.description && <p className="text-kova-silv text-xs mt-1 line-clamp-2">{signal.description}</p>}
            <div className="flex items-center gap-3 mt-2 text-kova-slate font-mono text-[9px] tracking-wide">
              {signal.metadata?.confidence != null && (
                <span>Conf: {(signal.metadata.confidence * 100).toFixed(0)}%</span>
              )}
              {signal.metadata?.systems_affected && (
                <span>{signal.metadata.systems_affected} system{signal.metadata.systems_affected > 1 ? 's' : ''}</span>
              )}
              {signal.metadata?.time_window && <span>{signal.metadata.time_window}</span>}
              {signal.published_at && (
                <span>{new Date(signal.published_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {isFeed && (
            <button
              onClick={() => onAcknowledge(signal.signal_id)}
              className="p-1.5 text-kova-slate hover:text-kova-cyan transition-colors"
              title="Acknowledge"
            >
              <CheckCircle size={16} />
            </button>
          )}
          {!isFeed && signal.status !== 'archived' && (
            <button
              onClick={() => onArchive(signal.signal_id)}
              className="p-1.5 text-kova-slate hover:text-kova-amber transition-colors"
              title="Archive"
            >
              <Archive size={16} />
            </button>
          )}
        </div>
      </div>
      {isFeed && signal.acknowledged_count > 0 && (
        <div className="mt-2 pt-2 border-t border-kova-cyan/6 flex items-center gap-1.5 text-kova-slate">
          <Eye size={12} />
          <span className="font-mono text-[9px] tracking-wide">{signal.acknowledged_count} constellation{signal.acknowledged_count > 1 ? 's' : ''} acknowledged</span>
        </div>
      )}
    </motion.div>
  );
}

function CaptureModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    signal_type: 'anomaly_pattern',
    title: '',
    description: '',
    severity: 'medium',
    systems_affected: 1,
    time_window: '24h',
    confidence: 0.5,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/signals/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Failed to capture signal');
        setSubmitting(false);
        return;
      }
      onSuccess();
    } catch (e) {
      setError('Network error');
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-kova-card border border-kova-cyan/12 p-6 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="capture-modal"
      >
        <h2 className="font-heading text-lg font-bold text-kova-white mb-4">Capture Signal</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Signal Type</label>
            <select
              data-testid="capture-type"
              value={form.signal_type}
              onChange={(e) => setForm(p => ({ ...p, signal_type: e.target.value }))}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
            >
              {Object.entries(SIGNAL_TYPE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Title</label>
            <input
              data-testid="capture-title"
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Elevated error rate in payment API"
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan"
            />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Description</label>
            <textarea
              data-testid="capture-description"
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Severity</label>
              <select
                data-testid="capture-severity"
                value={form.severity}
                onChange={(e) => setForm(p => ({ ...p, severity: e.target.value }))}
                className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
              >
                {['low', 'medium', 'high', 'critical'].map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Confidence</label>
              <input
                data-testid="capture-confidence"
                type="number"
                min="0" max="1" step="0.05"
                value={form.confidence}
                onChange={(e) => setForm(p => ({ ...p, confidence: parseFloat(e.target.value) || 0.5 }))}
                className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Systems Affected</label>
              <input
                type="number" min="1" max="100"
                value={form.systems_affected}
                onChange={(e) => setForm(p => ({ ...p, systems_affected: parseInt(e.target.value) || 1 }))}
                className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
              />
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Time Window</label>
              <select
                value={form.time_window}
                onChange={(e) => setForm(p => ({ ...p, time_window: e.target.value }))}
                className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
              >
                {['1h', '6h', '12h', '24h', '48h', '7d', '30d'].map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <p className="text-kova-red font-mono text-xs">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-4 py-2 hover:border-kova-cyan/25 hover:text-kova-white transition-colors">
              Cancel
            </button>
            <button
              data-testid="capture-submit"
              type="submit"
              disabled={submitting}
              className="font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Publishing...' : 'Publish Signal'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
